import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { toPercentage, toTruncated } from '@/utilities/formatting';
import { format } from 'date-fns';
import { useSelector, shallowEqual } from 'react-redux';
import { Data, Section, Table } from './styled';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import {
  Question,
  Link,
  ChevronRightIcon,
  Term,
  TooltipTitle,
} from '@/pages/CompanyReport/components/Overview/components/Fundamentals/styled';
import { COMPANY_PAST_ANCHOR } from '@/constants/anchors';
import { pathConstructor } from '@/hooks/useCompany/utils';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useCompanyReportPageUrl } from '@/pages/CompanyReport/hooks/useCompanyReportPageUrl';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

interface ReduxState {
  company: CompanyState;
  location: LocationState;
}
function selector(state: ReduxState) {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const analysis = state.company.analysis[companyId];
    const summary = state.company.summary[companyId];
    return {
      name: summary.tickerSymbol,
      eps: analysis.EPS,
      grossProfitMargin: analysis.pastGrossProfitMargin,
      netProfitMargin: analysis.pastNetIncomeMargin,
      debtEquityRatio: analysis.debtEquityRatio,
      lastEarningsUpdate: analysis.lastEarningsUpdateTimeStamp,
      nextEarningsUpdate: analysis.nextEarningsUpdateTimeStamp,
    };
  } catch (e) {
    return undefined;
  }
}

export function IncomeDetails() {
  const term = useGlossaryTerms();
  const debt2equity = term('debt to equity ratio');
  const profitMargin = term('profit margins');
  //temp tooltips
  const grossMargin = term('gross margin');
  const earningsPerShare = term('earnings per share');
  const data = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const companyUrl = useCompanyReportPageUrl();
  const { loadSection } = useVisibleSectionsContext();

  const handlePastLink = () => {
    loadSection('past');
  };

  const t = useOverviewTranslation();

  if (data === undefined) {
    return null;
  }
  const {
    name,
    eps,
    grossProfitMargin,
    netProfitMargin,
    debtEquityRatio,
    lastEarningsUpdate,
    nextEarningsUpdate,
  } = data;

  return (
    <Section>
      <div>
        <p>{t('fundamentals.income.details.lastReportedEarnings')}</p>
        <p>
          <Data>
            {lastEarningsUpdate
              ? format(lastEarningsUpdate, 'MMM dd, yyyy')
              : t('fundamentals.income.details.notAvailable')}
          </Data>
        </p>
      </div>
      <div>
        <p>{t('fundamentals.income.details.nextEarnings')}</p>
        <p>
          <Data>
            {nextEarningsUpdate
              ? format(nextEarningsUpdate, 'MMM dd, yyyy')
              : t('fundamentals.income.details.notAvailable')}
          </Data>
        </p>
      </div>
      <Table>
        <tbody>
          <tr>
            <td>
              <Tooltip
                title={<TooltipTitle>{earningsPerShare}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.earningsPerShare')}</Term>
              </Tooltip>
            </td>
            <td>{toTruncated(eps, 2)}</td>
          </tr>
          <tr>
            <td>
              <Tooltip
                title={<TooltipTitle>{grossMargin}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.grossMargin')}</Term>
              </Tooltip>
            </td>
            <td>{toPercentage(grossProfitMargin, 2)}</td>
          </tr>
          <tr>
            <td>
              <Tooltip
                title={<TooltipTitle>{profitMargin}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.netProfitMargin')}</Term>
              </Tooltip>
            </td>
            <td>{toPercentage(netProfitMargin, 2)}</td>
          </tr>
          <tr>
            <td>
              <Tooltip
                title={<TooltipTitle>{debt2equity}</TooltipTitle>}
                light
                openDelay={300}
              >
                <Term>{t('fundamentals.income.details.debtEquityRatio')}</Term>
              </Tooltip>
            </td>
            <td>{toPercentage(debtEquityRatio, 1)}</td>
          </tr>
        </tbody>
      </Table>
      <Question>
        {t('fundamentals.income.details.longTermPerformance', { name })}
      </Question>
      <Link
        data-cy-id="overview-fundamentals-historical-link"
        to={`${companyUrl}/${COMPANY_PAST_ANCHOR}`}
        onClick={handlePastLink}
      >
        {t('fundamentals.income.details.historicalLink')}
        <ChevronRightIcon />
      </Link>
    </Section>
  );
}
